import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Ic, ICONS } from '../data/icons';
import { Card, CardHeader, Avatar, Badge, Modal, toast, SearchInput, TableSkeleton, Pagination, EmptyState, FormField, Input, Select } from '../components/shared';
import { fetchUsers, createUser, updateUser, deleteUser, approveUser, rejectUser, fetchPendingUsers } from '../api/client';

const ROLES = ['Yönetici','Müdahale','Editör','Okuyucu'];
const ALL_ROLES = ['Süper Admin','Yönetici','Müdahale','Editör','Okuyucu'];
const DEPTS = ['Mühendislik','Pazarlama','Satış','Finans','İK','Tasarım','Hukuk','Operasyon','Genel'];
const ROLE_COLORS = { 'Süper Admin':'#ef4444','Yönetici':'#f59e0b','Müdahale':'#a78bfa','Editör':'#3b82f6','Okuyucu':'#22c55e' };
const STATUS_COLORS = { approved:'#22c55e', pending:'#f59e0b', rejected:'#ef4444' };
const STATUS_LABELS = { approved:'Onaylı', pending:'Beklemede', rejected:'Reddedildi' };
const PER_PAGE = 8;
const emptyForm = { name:'', email:'', role:'Okuyucu', dept:'Genel', phone:'' };

export default function Users({ C, currentUser }) {
  const isSuperAdmin = currentUser?.role === 'Süper Admin';
  const isAdmin = isSuperAdmin || currentUser?.role === 'Yönetici';

  const [tab, setTab] = useState('users'); // 'users' | 'pending'
  const [users, setUsers]           = useState([]);
  const [pending, setPending]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [search, setSearch]         = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [deptFilter, setDeptFilter] = useState('all');
  const [page, setPage]             = useState(1);
  const [addOpen, setAddOpen]       = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [form, setForm]             = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving]         = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  const loadUsers = useCallback(() => {
    setLoading(true);
    fetchUsers()
      .then(data => setUsers(Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : []))
      .catch(() => toast.error('Kullanıcılar yüklenemedi'))
      .finally(() => setLoading(false));
  }, []);

  const loadPending = useCallback(() => {
    if (!isSuperAdmin) return;
    setPendingLoading(true);
    fetchPendingUsers()
      .then(data => setPending(Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setPendingLoading(false));
  }, [isSuperAdmin]);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { if (isSuperAdmin) loadPending(); }, [isSuperAdmin, loadPending]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return users.filter(u => {
      const ms = !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
      const mr = roleFilter === 'all' || u.role === roleFilter;
      const md = deptFilter === 'all' || u.dept === deptFilter;
      return ms && mr && md;
    });
  }, [users, search, roleFilter, deptFilter]);

  const paged = filtered.slice((page-1)*PER_PAGE, page*PER_PAGE);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Ad Soyad zorunlu';
    else if (form.name.trim().split(' ').length < 2) e.name = 'Ad ve soyad girin';
    if (!form.email.trim()) e.email = 'E-posta zorunlu';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Geçerli e-posta girin';
    else if (!editTarget && users.find(u => u.email === form.email)) e.email = 'Bu e-posta zaten kayıtlı';
    setFormErrors(e);
    return !Object.keys(e).length;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (editTarget) {
        const updated = await updateUser(editTarget.id, form);
        setUsers(us => us.map(u => u.id === editTarget.id ? { ...u, ...updated } : u));
        toast.success('Kullanıcı güncellendi');
        setEditTarget(null);
      } else {
        const created = await createUser(form);
        setUsers(us => [...us, created]);
        toast.success('Kullanıcı eklendi');
        setAddOpen(false);
        setForm(emptyForm);
      }
    } catch (err) { toast.error(err.message || 'İşlem başarısız'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteUser(deleteTarget.id);
      setUsers(us => us.filter(u => u.id !== deleteTarget.id));
      toast.success('Kullanıcı silindi');
    } catch (err) { toast.error(err.message || 'Silme başarısız'); }
    setDeleteTarget(null);
  };

  const handleApprove = async (userId, userName) => {
    setActionLoading(a => ({ ...a, [userId]: 'approve' }));
    try {
      await approveUser(userId);
      setPending(ps => ps.filter(p => p.id !== userId));
      toast.success(`${userName} onaylandı ✅`);
      loadUsers();
    } catch (err) { toast.error(err.message || 'Onaylama başarısız'); }
    finally { setActionLoading(a => ({ ...a, [userId]: null })); }
  };

  const handleReject = async (reason) => {
    if (!rejectTarget) return;
    setActionLoading(a => ({ ...a, [rejectTarget.id]: 'reject' }));
    try {
      await rejectUser(rejectTarget.id, reason);
      setPending(ps => ps.filter(p => p.id !== rejectTarget.id));
      toast.info(`${rejectTarget.name} reddedildi`);
    } catch (err) { toast.error(err.message || 'İşlem başarısız'); }
    finally { setActionLoading(a => ({ ...a, [rejectTarget.id]: null })); setRejectTarget(null); }
  };

  const UserForm = () => (
    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <FormField label='Ad Soyad' required C={C} error={formErrors.name}>
          <Input C={C} error={formErrors.name} value={form.name} onChange={e=>{setForm(f=>({...f,name:e.target.value}));setFormErrors(fe=>({...fe,name:''}));}} placeholder='Ahmet Kaya' />
        </FormField>
        <FormField label='E-posta' required C={C} error={formErrors.email}>
          <Input C={C} error={formErrors.email} type='email' value={form.email} onChange={e=>{setForm(f=>({...f,email:e.target.value}));setFormErrors(fe=>({...fe,email:''}));}} placeholder='ahmet@sirket.com' disabled={!!editTarget} />
        </FormField>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <FormField label='Rol' C={C}>
          <Select C={C} value={form.role} onChange={e=>setForm(f=>({...f,role:e.target.value}))}>
            {(isSuperAdmin ? ALL_ROLES : ROLES).map(r=><option key={r} value={r}>{r}</option>)}
          </Select>
        </FormField>
        <FormField label='Departman' C={C}>
          <Select C={C} value={form.dept} onChange={e=>setForm(f=>({...f,dept:e.target.value}))}>
            {DEPTS.map(d=><option key={d} value={d}>{d}</option>)}
          </Select>
        </FormField>
      </div>
      <FormField label='Telefon' C={C}>
        <Input C={C} value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} placeholder='+90 555 000 0000' />
      </FormField>
    </div>
  );

  // ── Onay Bekleyenler Tablosu ─────────────────────────────────────────────────
  const PendingTab = () => {
    const [rejectReason, setRejectReason] = useState('');
    return (
      <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
        {pendingLoading ? (
          <Card C={C}><div style={{ padding:16 }}><TableSkeleton rows={3} cols={5} C={C} /></div></Card>
        ) : pending.length === 0 ? (
          <Card C={C}>
            <EmptyState icon='check' title='Bekleyen üyelik yok' desc='Tüm üyelik istekleri işlendi.' C={C} />
          </Card>
        ) : pending.map(u => (
          <div key={u.id} style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:14, padding:'16px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:16, flexWrap:'wrap' }}>
            <div style={{ display:'flex', alignItems:'center', gap:12, flex:1, minWidth:200 }}>
              <Avatar initials={u.name?.split(' ').map(s=>s[0]).join('').slice(0,2).toUpperCase()||'?'} size={42} />
              <div>
                <div style={{ fontSize:14.5, fontWeight:700, color:C.text }}>{u.name}</div>
                <div style={{ fontSize:12, color:C.textFaint, marginTop:2 }}>{u.email}</div>
                <div style={{ fontSize:11, color:C.textFaint, marginTop:2 }}>📁 {u.dept} · 🕐 {u.lastActive || 'az önce'}</div>
              </div>
            </div>
            <div style={{ display:'flex', gap:8, flexShrink:0 }}>
              <button onClick={() => handleApprove(u.id, u.name)} disabled={actionLoading[u.id]} style={{ padding:'9px 20px', borderRadius:9, border:'1px solid rgba(34,197,94,0.3)', background:'rgba(34,197,94,0.1)', color:C.green, fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:6, opacity:actionLoading[u.id]?0.6:1 }}>
                {actionLoading[u.id]==='approve' ? '...' : <><Ic d={ICONS.check} size={13} s={C.green} /> Onayla</>}
              </button>
              <button onClick={() => setRejectTarget(u)} disabled={actionLoading[u.id]} style={{ padding:'9px 20px', borderRadius:9, border:`1px solid ${C.red}44`, background:C.redBg, color:C.red, fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:6, opacity:actionLoading[u.id]?0.6:1 }}>
                {actionLoading[u.id]==='reject' ? '...' : <><Ic d={ICONS.x} size={13} s={C.red} /> Reddet</>}
              </button>
            </div>
          </div>
        ))}

        {/* Reject modal */}
        <Modal isOpen={!!rejectTarget} onClose={()=>setRejectTarget(null)} onConfirm={()=>handleReject(rejectReason)}
          title={`${rejectTarget?.name} Reddedilsin mi?`}
          type='danger' C={C} confirmLabel='Reddet'>
          <div>
            <div style={{ fontSize:13, color:C.textSub, marginBottom:12 }}>Reddetme sebebi (isteğe bağlı):</div>
            <textarea rows={3} value={rejectReason} onChange={e=>setRejectReason(e.target.value)} placeholder='Örn: Hesap bilgileri eksik, lütfen tekrar başvurun...'
              style={{ width:'100%', borderRadius:9, border:`1px solid ${C.border}`, background:C.surface2, color:C.text, fontSize:13, padding:'9px 12px', fontFamily:'inherit', outline:'none', resize:'vertical' }} />
          </div>
        </Modal>
      </div>
    );
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      {/* Tab navigation — Süper Admin'e özel "Onay Bekleyenler" sekmesi */}
      {isSuperAdmin && (
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={()=>setTab('users')} style={{ padding:'9px 20px', borderRadius:9, border:`1px solid ${tab==='users'?C.accentBorder:C.border}`, background:tab==='users'?C.accentBg:'transparent', color:tab==='users'?C.accent:C.textSub, fontSize:13, fontWeight:tab==='users'?700:400, cursor:'pointer', display:'flex', alignItems:'center', gap:7 }}>
            <Ic d={ICONS.users} size={14} /> Tüm Kullanıcılar
          </button>
          <button onClick={()=>setTab('pending')} style={{ padding:'9px 20px', borderRadius:9, border:`1px solid ${tab==='pending'?'rgba(245,158,11,0.4)':C.border}`, background:tab==='pending'?'rgba(245,158,11,0.1)':'transparent', color:tab==='pending'?C.amber:C.textSub, fontSize:13, fontWeight:tab==='pending'?700:400, cursor:'pointer', display:'flex', alignItems:'center', gap:7, position:'relative' }}>
            <Ic d={ICONS.bell} size={14} /> Onay Bekleyenler
            {pending.length > 0 && <span style={{ background:'#f59e0b', color:'#000', fontSize:10, fontWeight:800, padding:'1px 6px', borderRadius:99, marginLeft:2 }}>{pending.length}</span>}
          </button>
        </div>
      )}

      {tab === 'pending' && isSuperAdmin ? <PendingTab /> : (
        <>
          {/* Toolbar */}
          <div style={{ display:'flex', flexWrap:'wrap', gap:10, alignItems:'center' }}>
            <SearchInput value={search} onChange={v=>{setSearch(v);setPage(1);}} placeholder='Kullanıcı veya e-posta ara...' C={C} style={{ flex:'1 1 200px', maxWidth:320 }} />
            <select value={roleFilter} onChange={e=>{setRoleFilter(e.target.value);setPage(1);}} style={{ height:38, borderRadius:9, border:`1px solid ${C.border}`, background:C.surface, color:C.text, fontSize:12.5, padding:'0 10px', fontFamily:'inherit', cursor:'pointer' }}>
              <option value='all'>Tüm Roller</option>
              {ALL_ROLES.map(r=><option key={r} value={r}>{r}</option>)}
            </select>
            <select value={deptFilter} onChange={e=>{setDeptFilter(e.target.value);setPage(1);}} style={{ height:38, borderRadius:9, border:`1px solid ${C.border}`, background:C.surface, color:C.text, fontSize:12.5, padding:'0 10px', fontFamily:'inherit', cursor:'pointer' }}>
              <option value='all'>Tüm Departmanlar</option>
              {DEPTS.map(d=><option key={d} value={d}>{d}</option>)}
            </select>
            {isAdmin && (
              <button onClick={()=>{setForm(emptyForm);setFormErrors({});setAddOpen(true);}} style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:7, padding:'8px 18px', borderRadius:9, border:'none', background:C.accent, color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>
                <Ic d={ICONS.userPlus} size={15} s='#fff' /> Kullanıcı Ekle
              </button>
            )}
          </div>

          {/* Stats */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:10 }}>
            {[['Toplam',users.length,C.accent,'users'],['Yönetici',users.filter(u=>['Yönetici','Süper Admin'].includes(u.role)).length,C.amber,'shield'],['Aktif',users.filter(u=>!u.lastActive?.includes('gün')).length,C.green,'check'],isSuperAdmin&&['Bekleyen',pending.length,C.amber,'bell']].filter(Boolean).map(([l,v,col,ic])=>(
              <div key={l} style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:'14px 18px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div>
                  <div style={{ fontSize:24, fontWeight:800, color:C.text, letterSpacing:'-1px' }}>{v}</div>
                  <div style={{ fontSize:12, color:C.textFaint, marginTop:2 }}>{l}</div>
                </div>
                <div style={{ width:36, height:36, borderRadius:10, background:`${col}20`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <Ic d={ICONS[ic]} size={16} s={col} />
                </div>
              </div>
            ))}
          </div>

          {/* Table */}
          <Card C={C}>
            <CardHeader C={C} title='Kullanıcı Listesi' action={<span style={{ fontSize:11, color:C.textFaint }}>Toplam {filtered.length} kullanıcı</span>} />
            {loading ? <TableSkeleton rows={6} cols={6} C={C} /> : (
              <>
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', minWidth:680 }}>
                    <thead>
                      <tr style={{ background:C.surface2 }}>
                        {['Kullanıcı','E-posta','Rol','Departman','Cihazlar','Son Aktif','İşlem'].map(h=>(
                          <th key={h} style={{ textAlign:'left', padding:'10px 16px', fontSize:10.5, fontWeight:700, color:C.textFaint, letterSpacing:'0.7px', textTransform:'uppercase', borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paged.map(u=>(
                        <tr key={u.id} style={{ transition:'background 0.1s', cursor:'pointer' }} onClick={()=>setDetailTarget(u)} onMouseEnter={e=>e.currentTarget.style.background=C.surface2} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                          <td style={{ padding:'12px 16px', borderBottom:`1px solid ${C.border}` }}>
                            <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                              <Avatar initials={u.name?.split(' ').map(s=>s[0]).join('').slice(0,2).toUpperCase()||'?'} size={30} />
                              <div>
                                <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{u.name}</div>
                                {isSuperAdmin && u.status !== 'approved' && <Badge color={STATUS_COLORS[u.status]} small>{STATUS_LABELS[u.status]}</Badge>}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding:'12px 16px', borderBottom:`1px solid ${C.border}` }}><span style={{ fontSize:12, color:C.textSub }}>{u.email}</span></td>
                          <td style={{ padding:'12px 16px', borderBottom:`1px solid ${C.border}` }}><Badge color={ROLE_COLORS[u.role]||C.accent}>{u.role}</Badge></td>
                          <td style={{ padding:'12px 16px', borderBottom:`1px solid ${C.border}` }}><span style={{ fontSize:12, color:C.textSub }}>{u.dept}</span></td>
                          <td style={{ padding:'12px 16px', borderBottom:`1px solid ${C.border}` }}><span style={{ fontSize:12, color:C.textSub }}>{u.devices||0} cihaz</span></td>
                          <td style={{ padding:'12px 16px', borderBottom:`1px solid ${C.border}` }}><span style={{ fontSize:11, color:C.textFaint }}>{u.lastActive}</span></td>
                          <td style={{ padding:'12px 16px', borderBottom:`1px solid ${C.border}` }} onClick={e=>e.stopPropagation()}>
                            <div style={{ display:'flex', gap:6 }}>
                              {isAdmin && <button onClick={()=>{setForm({name:u.name,email:u.email,role:u.role,dept:u.dept,phone:u.phone||''});setFormErrors({});setEditTarget(u);}} style={{ padding:'4px 10px', borderRadius:7, border:`1px solid ${C.border}`, cursor:'pointer', fontSize:11, background:'transparent', color:C.textSub, fontWeight:600 }}>Düzenle</button>}
                              {isSuperAdmin && <button onClick={()=>setDeleteTarget(u)} style={{ padding:'4px 10px', borderRadius:7, border:`1px solid ${C.red}44`, cursor:'pointer', fontSize:11, background:C.redBg, color:C.red, fontWeight:600 }}>Sil</button>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {filtered.length===0 && <EmptyState icon='users' title='Kullanıcı bulunamadı' desc='Arama veya filtre kriterlerini değiştirin' C={C} />}
                <Pagination page={page} total={filtered.length} perPage={PER_PAGE} onChange={setPage} C={C} />
              </>
            )}
          </Card>
        </>
      )}

      {/* Modals */}
      <Modal isOpen={addOpen} onClose={()=>setAddOpen(false)} onConfirm={handleSave} title='Yeni Kullanıcı Ekle' type='info' C={C} confirmLabel={saving?'Kaydediliyor...':'Kullanıcı Ekle'} wide><UserForm /></Modal>
      <Modal isOpen={!!editTarget} onClose={()=>setEditTarget(null)} onConfirm={handleSave} title='Kullanıcıyı Düzenle' type='info' C={C} confirmLabel={saving?'Kaydediliyor...':'Güncelle'} wide><UserForm /></Modal>
      <Modal isOpen={!!deleteTarget} onClose={()=>setDeleteTarget(null)} onConfirm={handleDelete}
        title='Kullanıcıyı Sil'
        message={`"${deleteTarget?.name}" adlı kullanıcıyı kalıcı olarak silmek istediğinizden emin misiniz?`}
        type='danger' C={C} confirmLabel='Evet, Sil' />

      {/* Detail modal */}
      <Modal isOpen={!!detailTarget} onClose={()=>setDetailTarget(null)} title={detailTarget?.name||''} type='info' C={C} wide>
        {detailTarget && (
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 16px', background:C.surface2, borderRadius:12 }}>
              <Avatar initials={detailTarget.name?.split(' ').map(s=>s[0]).join('').slice(0,2).toUpperCase()||'?'} size={48} />
              <div>
                <div style={{ fontSize:16, fontWeight:800, color:C.text }}>{detailTarget.name}</div>
                <div style={{ fontSize:12, color:C.textFaint, marginTop:2 }}>{detailTarget.email}</div>
                <div style={{ marginTop:6, display:'flex', gap:6, flexWrap:'wrap' }}>
                  <Badge color={ROLE_COLORS[detailTarget.role]||C.accent}>{detailTarget.role}</Badge>
                  <Badge color={C.textSub}>{detailTarget.dept}</Badge>
                  {isSuperAdmin && <Badge color={STATUS_COLORS[detailTarget.status]}>{STATUS_LABELS[detailTarget.status]}</Badge>}
                </div>
              </div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              {[['Cihaz Sayısı',`${detailTarget.devices||0} cihaz`],['Son Aktif',detailTarget.lastActive||'-'],['Departman',detailTarget.dept],['Rol',detailTarget.role]].map(([l,v])=>(
                <div key={l} style={{ padding:'12px 14px', background:C.surface2, borderRadius:10 }}>
                  <div style={{ fontSize:11, color:C.textFaint, marginBottom:4 }}>{l}</div>
                  <div style={{ fontSize:13.5, fontWeight:600, color:C.text }}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:8 }}>
              {isAdmin && <button onClick={()=>{setForm({name:detailTarget.name,email:detailTarget.email,role:detailTarget.role,dept:detailTarget.dept,phone:detailTarget.phone||''});setFormErrors({});setEditTarget(detailTarget);setDetailTarget(null);}} style={{ padding:'8px 16px', borderRadius:9, border:`1px solid ${C.border}`, background:'transparent', color:C.textSub, fontSize:13, fontWeight:600, cursor:'pointer' }}>Düzenle</button>}
              {isSuperAdmin && <button onClick={()=>{setDeleteTarget(detailTarget);setDetailTarget(null);}} style={{ padding:'8px 16px', borderRadius:9, border:`1px solid ${C.red}44`, background:C.redBg, color:C.red, fontSize:13, fontWeight:600, cursor:'pointer' }}>Sil</button>}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
